import { Controller, useForm } from 'react-hook-form';
import { add, format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowRight, faStopwatch } from '@fortawesome/pro-regular-svg-icons';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import Select, { Option } from '@v2/shared/components/Select';
import TextButton from '@v2/shared/components/TextButton';
import CancelWarningBox from './CancelWarningBox';
import { AutoRenewalDetail } from './types';

const DATE_FORMAT = 'MMMM do, yyyy';

const DELAY_DURATIONS: Option[] = [
  { value: 1, label: '1 Month' },
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '1 Year' },
];

type Props = {
  autoRenewal: AutoRenewalDetail;
  autoRenewServiceName: string;
  onDelay: (months: number) => void;
  loading?: boolean;
  error?: Error;
  /** Set when this form is the disable flow's "take a break instead" step. */
  showDisableFunctionality?: boolean;
  onContinueToDisable?: () => void;
  onGoBack?: () => void;
  disableLoading?: boolean;
};

/**
 * Push the next renewal back by up to a year, keeping the locked-in rate.
 *
 * The same form serves two callers: its own modal, opened from the "Delay" button, and
 * the disable flow, where it is what a customer is offered instead of cancelling. The
 * disable-flow extras are the props below — a way back, and the warning box that lets
 * them cancel anyway — and everything else is identical, which is why it is one form.
 */
export default function DelayAutoRenewalForm({
  autoRenewal,
  autoRenewServiceName,
  onDelay,
  loading = false,
  error,
  showDisableFunctionality,
  onContinueToDisable,
  onGoBack,
  disableLoading = false,
}: Props) {
  const { control, handleSubmit, watch } = useForm<{ months: Option }>({
    defaultValues: { months: DELAY_DURATIONS[0] },
  });

  // Absent on a subscription with no charge scheduled, and there is then nothing to
  // compare: the delay is still a legal thing to ask for, so the form stays and only the
  // before/after line goes. `parseISO` rather than `new Date` for the reason the whole
  // page uses it — see BillingHistory.
  const chargeDate = autoRenewal.automatic_charge_date ? parseISO(autoRenewal.automatic_charge_date) : null;
  const months = watch('months').value as number;

  return (
    <div className="p-5 md:p-8">
      <h2 className="font-semibold text-2xl mb-2 tracking-tight text-center">
        Delay {autoRenewServiceName}
        {showDisableFunctionality && ' Instead'}
      </h2>
      <p className="mb-6 font-medium text-center">
        You can delay your {autoRenewServiceName} renewal for 1 to 12 months. That way you still get a break, but
        won&apos;t lose your locked-in rate.
      </p>

      {error && <Alert color="danger">{error.message}</Alert>}

      <form onSubmit={handleSubmit(({ months: selected }) => onDelay(selected.value as number))} noValidate>
        <div className="flex items-center justify-center">
          <div className="max-w-[350px] w-full">
            <span className="font-semibold text-sm">Delay for</span>
            <Controller
              name="months"
              control={control}
              render={({ field }) => (
                <Select
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={loading}
                  className="mb-5"
                  options={DELAY_DURATIONS}
                />
              )}
            />
          </div>
        </div>

        {chargeDate && (
          <RenewalDateComparison
            currentDate={format(chargeDate, DATE_FORMAT)}
            newDate={format(add(chargeDate, { months }), DATE_FORMAT)}
          />
        )}

        <div className="flex flex-col items-center justify-center">
          <Button
            type="submit"
            isLoading={loading}
            icon={faStopwatch}
            loadingText="Delaying..."
            className="mb-3 text-sm"
          >
            Delay {autoRenewServiceName}
          </Button>
        </div>
      </form>

      {showDisableFunctionality && onGoBack && (
        <div className="flex justify-center mb-4">
          <TextButton onClick={onGoBack} className="text-sm">
            ← Go Back
          </TextButton>
        </div>
      )}

      {showDisableFunctionality && onContinueToDisable && (
        <CancelWarningBox
          autoRenewServiceName={autoRenewServiceName}
          onDisable={onContinueToDisable}
          loading={disableLoading}
        />
      )}
    </div>
  );
}

/** What the renewal date is now, and what it would become. */
function RenewalDateComparison({ currentDate, newDate }: { currentDate: string; newDate: string }) {
  return (
    <div className="mb-8 font-medium text-sm text-center">
      <div className="mb-1 flex items-center justify-center">
        <span className="text-slate-600">
          <div className="inline-block font-semibold line-through">{currentDate}</div>
          <div className="text-xs">Current Renewal Date</div>
        </span>
        <span className="px-2">
          <FontAwesomeIcon icon={faLongArrowRight} />
        </span>
        <span>
          <div className="inline-block font-semibold text-green-600">{newDate}</div>
          <div className="text-xs text-green-600 italic font-semibold">New Renewal Date</div>
        </span>
      </div>
    </div>
  );
}
